import React from "react";
import { Flame, Snowflake, BarChart3 } from "lucide-react";
import Layout from "../components/Layout";
import useSeo from "../hooks/useSeo";
import { useAnalytics } from "../hooks/useApi";

const NumChip = ({ num, count, tone }) => (
  <div
    className="flex flex-col items-center justify-center rounded-lg py-3 border"
    style={{
      backgroundColor: tone === "hot" ? "#fff1ec" : "#eef5ff",
      borderColor: tone === "hot" ? "#f3c4b4" : "#c5dbf3",
    }}
  >
    <span className="text-2xl font-bold text-[#1f2a37]">{num}</span>
    <span className="text-xs text-gray-500">{count}x</span>
  </div>
);

const Analytics = () => {
  const { data } = useAnalytics();
  useSeo(
    "Morning Sunday Teer Analytics | Shillong Teer Hot & Cold Numbers",
    "Morning Sunday Teer number statistics — hot numbers, cold numbers and F/R & S/R frequency analysis from historical Shillong Teer results."
  );

  return (
    <Layout title="Analytics">
      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="flex items-center gap-2 mb-1">
          <BarChart3 className="w-6 h-6 text-[#2f5d84]" />
          <h1 className="text-xl font-bold text-[#1f2a37]">
            Morning Sunday Teer Number Statistics
          </h1>
        </div>
        <p className="text-sm text-gray-500 mb-6">
          Auto-generated from {data?.total ?? 0} recorded round outcomes. These
          numbers describe the past — they are a historical record, not a
          forecast.
        </p>

        {!data ? (
          <p className="text-gray-400">Loading...</p>
        ) : (
          <div className="space-y-8">
            <section>
              <div className="flex items-center gap-2 mb-3">
                <Flame className="w-5 h-5 text-[#e5342a]" />
                <h2 className="font-bold text-[#1f2a37]">Hot Numbers (most frequent)</h2>
              </div>
              <div className="grid grid-cols-5 gap-2">
                {data.hot.map((h) => (
                  <NumChip key={h.num} num={h.num} count={h.count} tone="hot" />
                ))}
              </div>
            </section>

            <section>
              <div className="flex items-center gap-2 mb-3">
                <Snowflake className="w-5 h-5 text-[#2f5d84]" />
                <h2 className="font-bold text-[#1f2a37]">Cold Numbers (least frequent)</h2>
              </div>
              <div className="grid grid-cols-5 gap-2">
                {data.cold.map((c) => (
                  <NumChip key={c.num} num={c.num} count={c.count} tone="cold" />
                ))}
              </div>
            </section>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <section>
                <h2 className="font-bold text-[#1f2a37] mb-2">Top F/R Numbers</h2>
                <div className="border border-gray-200 rounded-md overflow-hidden">
                  {data.frTop.map((r, i) => (
                    <div key={r.num} className={`flex justify-between px-4 py-2 text-sm ${i % 2 ? "bg-gray-50" : "bg-white"}`}>
                      <span className="font-semibold">{r.num}</span>
                      <span className="text-gray-500">{r.count} times</span>
                    </div>
                  ))}
                </div>
              </section>
              <section>
                <h2 className="font-bold text-[#1f2a37] mb-2">Top S/R Numbers</h2>
                <div className="border border-gray-200 rounded-md overflow-hidden">
                  {data.srTop.map((r, i) => (
                    <div key={r.num} className={`flex justify-between px-4 py-2 text-sm ${i % 2 ? "bg-gray-50" : "bg-white"}`}>
                      <span className="font-semibold">{r.num}</span>
                      <span className="text-gray-500">{r.count} times</span>
                    </div>
                  ))}
                </div>
              </section>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Analytics;
